import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { usePlmProgramDetailEditQuery } from '../../../../types/generated-types';
import { usePlmProgramDetailEditSubSubscription } from '../../../../types/generated-types';
import BorderedSection from '../../shared/borderedSection';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

import '../ui/plm.css';
const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString();
};

export const PlmProgramDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();

  const { data, loading, error } = usePlmProgramDetailEditQuery({
    variables: { id: id! },
  });

  useEffect(() => {}, [can, navigate, id]);

  usePlmProgramDetailEditSubSubscription({
    variables: { id: id! },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const plmProgram = data?.plmProgramById;

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Error loading PLM program: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!plmProgram) {
    return (
      <Box p={3}>
        <Typography color="error">PLM program not found</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0,
        }}
      >
        <Tooltip title="Back to List">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plmPrograms')}
            sx={{ mr: 2 }}
          >
            Back to PLM Programs
          </Button>
        </Tooltip>

        <Tooltip title="Edit">
          <IconButton
            onClick={() => navigate(`/plmPrograms/${id}/edit-plmProgram`)}
            sx={{ ml: 2 }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        sx={{ padding: '10px' }}
        variant="h4"
        component="h1"
        gutterBottom
      >
        {plmProgram.name}
      </Typography>

      <Box sx={{ marginBottom: '30px' }}>
        <BorderedSection title="Basic Information">
          <Grid container className="plm-inner-grid-container" spacing={3}>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Program ID:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.programId}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Name:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.name}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Utility:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.partner
                  ? plmProgram.partner.name
                  : plmProgram.partnerId || 'No partner'}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Season:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.season || '-'}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Start Date:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.startDate ? formatDate(plmProgram.startDate) : '-'}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                End Date:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.endDate ? formatDate(plmProgram.endDate) : '-'}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Participation Type:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.participationType || '-'}
              </Typography>
            </Grid>
            <Grid item className="item-wrapper" xs={12} md={6}>
              <Typography className="label" component="div">
                Incentive:
              </Typography>
              <Typography className="sub-label" component="div">
                {plmProgram.incentiveDescription || '-'}
              </Typography>
            </Grid>
          </Grid>
        </BorderedSection>
      </Box>

      <BorderedSection
        containerStyle={{ marginTop: '22px' }}
        title="Event Configuration"
      >
        <Grid container className="plm-inner-grid-container" spacing={3}>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Notification Lead Time (minutes):
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.notificationLeadTimeMinutes || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Maximum Notification Lead Time (minutes):
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.maximumNotificationLeadTimeMinutes || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Minimum Event Duration (minutes):
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.minimumEventDurationMinutes || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Maximum Event Duration (minutes):
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.maximumEventDurationMinutes || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Minimum Events Per Season:
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.minimumEventsPerSeason || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Maximum Events Per Season:
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.maximumEventsPerSeason || '-'}
            </Typography>
          </Grid>
          <Grid item className="item-wrapper" xs={12} md={6}>
            <Typography className="label" component="div">
              Measurement Interval (minutes):
            </Typography>
            <Typography className="sub-label" component="div">
              {plmProgram.measurementIntervalMinutes || '-'}
            </Typography>
          </Grid>
        </Grid>
      </BorderedSection>
    </Box>
  );
};
